import { progressionStateSchema, type ProgressionStateRow } from '@cadencia/contracts';
import {
  assessStall,
  defaultStrategyFor,
  learnOverrideBias,
  stallCounterFromHistory,
  estimateCurrentE1rm,
  e1rmConfidence,
  e1rmEpleyRir,
  requireExercise,
  type ExerciseHistory,
  type OverrideObservation,
  type ProgressionState,
  type RepTarget,
  type StallAssessment,
} from '@cadencia/domain';
import { db } from '../db.js';
import { diaLocalDe, todayLocal } from '../../lib/dates.js';
import { newSyncColumns, putNewRows, putRow, type WriteContext } from '../writes.js';

/** Estado de progresion por (ejercicio, instancia de equipamiento). */
export async function getProgressionState(
  userId: string,
  exerciseSlug: string,
  equipmentId: string | null,
): Promise<ProgressionStateRow | undefined> {
  const rows = await db.progressionState.where('exerciseSlug').equals(exerciseSlug).toArray();
  return rows.find(
    (row) =>
      row.userId === userId && row.deletedAt === null && (row.equipmentId ?? null) === equipmentId,
  );
}

export interface ProgressionContext {
  exerciseSlug: string;
  equipmentId: string | null;
  equipmentKind: string;
  smallestIncrementKg: number | null;
  currentLoadKg: number | null;
  isReadaptation: boolean;
}

/** Estado vigente, o el inicial si el ejercicio todavia no tiene historia. */
export async function resolveProgressionState(
  userId: string,
  context: ProgressionContext,
): Promise<ProgressionState> {
  const stored = await getProgressionState(userId, context.exerciseSlug, context.equipmentId);
  if (stored) {
    return {
      strategy: stored.strategy,
      currentE1rmKg: stored.currentE1rmKg,
      consecutiveTopRange: stored.consecutiveTopRange,
      stallCounter: stored.stallCounter,
      overrideBiasPct: stored.overrideBiasPct,
    };
  }

  const exercise = requireExercise(context.exerciseSlug);
  return {
    strategy: defaultStrategyFor({
      equipmentKind: context.equipmentKind,
      isCompound: exercise.isCompound,
      smallestIncrementKg: context.smallestIncrementKg,
      currentLoadKg: context.currentLoadKg,
      isReadaptation: context.isReadaptation,
    }),
    currentE1rmKg: null,
    consecutiveTopRange: 0,
    stallCounter: 0,
    overrideBiasPct: 0,
  };
}

/**
 * Los overrides de ese (ejercicio, equipamiento), listos para el §7.2.
 *
 * POR QUE SE LEE AQUI Y NO SE SACA DEL HISTORIAL
 *
 * `ExerciseHistory` no lleva `recommendedLoadKg` ni `wasOverride` —solo carga,
 * repeticiones y RIR— y ademas se corta a las ultimas seis sesiones, que es
 * menos de lo que pide la ventana de 45 dias. Ampliar el historial para esto
 * habria metido dos campos que el motor de cargas no usa en todos sus
 * llamadores.
 *
 * EL RIR OBJETIVO SALE DE LA PRESCRIPCION, NO DE UN PROMEDIO
 *
 * La condicion de aceptacion del §7.2 compara el RIR conseguido con el
 * PROGRAMADO para esa serie, y eso vive en `set_prescriptions`. Una serie sin
 * prescripcion —las hay: el historial es mas viejo que algunas columnas— se
 * descarta en vez de suponerle un objetivo: inventarlo decidiria si el override
 * «funciono» con un numero que nadie escribio.
 *
 * `where('exerciseSlug')` esta indexado (ver `data/db.ts`); el resto se filtra
 * en memoria, que es lo mismo que hace `getExerciseHistory` con esta tabla.
 */
async function overridesDe(
  userId: string,
  exerciseSlug: string,
  equipmentId: string | null,
): Promise<OverrideObservation[]> {
  const series = (await db.performedSets.where('exerciseSlug').equals(exerciseSlug).toArray())
    .filter(
      (set) =>
        set.userId === userId &&
        set.deletedAt === null &&
        (set.equipmentId ?? null) === equipmentId &&
        set.wasOverride &&
        set.recommendedLoadKg !== null &&
        set.setPrescriptionId !== null,
    );
  if (series.length === 0) return [];

  const prescripciones = await db.setPrescriptions.bulkGet(
    series.map((set) => set.setPrescriptionId as string),
  );

  const observaciones: OverrideObservation[] = [];
  for (const [indice, set] of series.entries()) {
    const prescripcion = prescripciones[indice];
    if (prescripcion === undefined || prescripcion.deletedAt !== null) continue;
    observaciones.push({
      // El dia local se resuelve AQUI, que es donde se conoce el huso: la
      // ventana de 45 dias se cuenta en dias de calendario y `performedAt` es
      // un instante en UTC.
      localDate: diaLocalDe(set.performedAt),
      // El parte, no el dia: dos sesiones el mismo dia son dos ocasiones.
      sessionKey: set.workoutLogId,
      recommendedLoadKg: set.recommendedLoadKg as number,
      loadKg: set.loadKg,
      rir: set.rir,
      targetRir: prescripcion.targetRir,
    });
  }
  return observaciones;
}

/**
 * Recalcula el estado a partir de los HECHOS (series registradas) al terminar
 * una sesion. Es una entidad derivada: ante divergencia se recalcula, no se
 * fusiona (docs/SYNC_ENGINE.md §5.4).
 */
export async function refreshProgressionState(
  ctx: WriteContext,
  input: {
    exerciseSlug: string;
    equipmentId: string | null;
    history: ExerciseHistory;
    context: ProgressionContext;
    sessionId: string | null;
    /**
     * Objetivo con el que se prescribio el ejercicio. Sin el no se puede
     * decidir si hay estancamiento: la comprobacion clave del §9 compara el RIR
     * real con el PROGRAMADO, y sin programado no hay con que comparar.
     */
    target?: RepTarget | undefined;
    /**
     * Finales de las ventanas en las que se evalua el estancamiento, de mas
     * antigua a mas reciente. Quien llama fija la cadencia; el §9 dice cuando
     * hay estancamiento, no cada cuanto sube el escalon.
     */
    stallCutoffs?: readonly string[] | undefined;
    /** Patrones principales estancados a la vez, para el cruce del §9. */
    simultaneousStalledPatterns?: number | undefined;
    aggressiveDeficit?: boolean | null | undefined;
    /** Dia local con el que se cuentan las ventanas del §7.2. Inyectable para los tests. */
    today?: string | undefined;
  },
): Promise<StallAssessment | null> {
  const sessionE1rms = input.history.sessions
    .map((session) => {
      let best: { at: string; e1rmKg: number; confidence: number } | null = null;
      for (const set of session.sets) {
        const value = e1rmEpleyRir(set);
        if (value <= 0) continue;
        if (best === null || value > best.e1rmKg) {
          best = { at: session.at, e1rmKg: value, confidence: e1rmConfidence(set) };
        }
      }
      return best;
    })
    .filter((entry): entry is { at: string; e1rmKg: number; confidence: number } => entry !== null);

  const current = estimateCurrentE1rm(sessionE1rms);
  const existing = await getProgressionState(ctx.userId, input.exerciseSlug, input.equipmentId);
  const strategy = existing?.strategy ?? (await resolveProgressionState(ctx.userId, input.context)).strategy;

  // Estancamiento (§9). Sin objetivo prescrito no se evalua y el contador se
  // deja como estaba: adivinar el objetivo seria decidir sobre un dato
  // inventado, y la respuesta a un estancamiento baja cargas.
  const target = input.target;
  const estancamiento =
    target === undefined
      ? null
      : assessStall({
          history: input.history,
          state: {
            strategy,
            currentE1rmKg: existing?.currentE1rmKg ?? null,
            consecutiveTopRange: existing?.consecutiveTopRange ?? 0,
            stallCounter: existing?.stallCounter ?? 0,
            overrideBiasPct: existing?.overrideBiasPct ?? 0,
          },
          target,
          // DERIVADO del historial, no acumulado sobre lo guardado. Esta funcion
          // se llama cada vez que se abre el panel de propuestas; con un
          // contador acumulativo, abrirlo cinco veces bastaria para llegar a la
          // revision estructural sin haber pisado el gimnasio.
          stallCounter: stallCounterFromHistory(input.history, target, input.stallCutoffs ?? []),
          simultaneousStalledPatterns: input.simultaneousStalledPatterns,
          aggressiveDeficit: input.aggressiveDeficit,
        });

  // Sesgo aprendido de los overrides (§7.2). Hasta el 12/09/2026 aqui habia un
  // `existing?.overrideBiasPct ?? 0` fijo: el campo se aplicaba en el motor, se
  // guardaba y se sincronizaba, y NADIE lo calculaba nunca, asi que el
  // multiplicador valia 1 desde la Fase 3. La regla entera vive en el dominio;
  // esto solo le pasa los hechos y el dia local.
  const sesgo = learnOverrideBias({
    observations: await overridesDe(ctx.userId, input.exerciseSlug, input.equipmentId),
    today: input.today ?? todayLocal(),
    previousBiasPct: existing?.overrideBiasPct ?? 0,
  });

  const row = progressionStateSchema.parse({
    ...(existing ?? newSyncColumns(ctx)),
    exerciseSlug: input.exerciseSlug,
    equipmentId: input.equipmentId,
    strategy,
    currentE1rmKg: current?.e1rmKg ?? null,
    e1rmTrendMonthly: existing?.e1rmTrendMonthly ?? null,
    consecutiveTopRange: existing?.consecutiveTopRange ?? 0,
    stallCounter: estancamiento?.stallCounter ?? existing?.stallCounter ?? 0,
    overrideBiasPct: sesgo.biasPct,
    lastUpdatedSession: input.sessionId,
  });

  if (existing) {
    // Solo se escribe si algo cambio de verdad. Cada `putRow` sube `rev` y
    // encola una operacion de sincronizacion; sin esta comprobacion, abrir el
    // panel de propuestas generaria trafico por cada ejercicio entrenado cada
    // vez, para dejar la fila exactamente igual.
    if (!mismoEstado(existing, row)) {
      await putRow(ctx, 'progressionState', row);
    }
  } else {
    await putNewRows(ctx, [{ table: 'progressionState', row }]);
  }

  return estancamiento;
}

/** Compara lo que de verdad describe el estado, ignorando columnas de sincronizacion. */
function mismoEstado(a: ProgressionStateRow, b: ProgressionStateRow): boolean {
  return (
    a.strategy === b.strategy &&
    a.currentE1rmKg === b.currentE1rmKg &&
    a.e1rmTrendMonthly === b.e1rmTrendMonthly &&
    a.consecutiveTopRange === b.consecutiveTopRange &&
    a.stallCounter === b.stallCounter &&
    a.overrideBiasPct === b.overrideBiasPct &&
    a.lastUpdatedSession === b.lastUpdatedSession
  );
}
